import type {Instant, UUID} from './common'
import type {EntityLink} from './entity-link'
import type {TriggerSource} from './identity'
/* eslint-disable i18next/no-literal-string -- const definitions are the source of truth */

import {BadgeVariant} from '@/components/ui/badge'
import {EnumValue} from '@/components/shared/EnumValue'
import {CheckCircle, AlertCircle, XCircle, Lock, Clock, type LucideIcon} from 'lucide-react'

export const AccountStatus = {
    PENDING: 'PENDING',
    ENABLED: 'ENABLED',
    DISABLED: 'DISABLED',
    LOCKED: 'LOCKED',
    DELETED: 'DELETED',
} as const;
export type AccountStatus = typeof AccountStatus[keyof typeof AccountStatus];

/**
 * Inline actor projection for {@link AccountStatusEvent}. {@code null} when
 * the event is system-driven (spec section 3.4 — {@code actor_id} nullable).
 */
export interface AccountStatusEventActor {
  id: UUID;
  displayName: string;
}

/**
 * One row of the {@code account_status_events} timeline. Append-only — spec
 * section 4.2, 7.2. Mirrors {@code IdentityLifecycleEvent} for the account side.
 */
export interface AccountStatusEvent {
  id: UUID;
  accountId: UUID;
  fromStatus: AccountStatus | null;
  toStatus: AccountStatus;
  changedAt: Instant;
  actor: AccountStatusEventActor | null;
  triggerSource: TriggerSource;
  triggerRefType: string | null;
  triggerRefId: UUID | null;
  reason: string | null;
}

/**
 * Account response from the API.
 */
export interface Account {
  id: UUID;
  identity: EntityLink;
  targetSystem: EntityLink;
  targetSystemType: string;
  externalIdentifier: string;
  status: AccountStatus;
  disabledAt: Instant | null;
  version: number;
  createdAt: Instant;
  updatedAt: Instant;
}

/**
 * Paginated response for accounts.
 */
export interface AccountPage {
  content: Account[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

/**
 * Create account request.
 */
export interface CreateAccountRequest {
  identityId: UUID;
  targetSystemId: UUID;
  externalIdentifier: string;
}

/**
 * Update account request.
 */
export interface UpdateAccountRequest {
  externalIdentifier: string;
  version: number;
}

/**
 * Rich enum carrying UI rendering metadata (badge variant + icon) for
 * {@link AccountStatus} wire values. The wire-type string-union remains the
 * shape used by API responses and test builders; this class is the single
 * source of truth for "how does this status look in the UI". Icon supports
 * the color-independent status display contract (WCAG 1.4.1).
 */
export class AccountStatusBadge extends EnumValue {
    constructor(
        code: string,
        variant: BadgeVariant,
        public readonly icon: LucideIcon,
    ) {
        super(code, variant)
    }

    static readonly PENDING = new AccountStatusBadge(AccountStatus.PENDING, BadgeVariant.OUTLINE, Clock)
    static readonly ENABLED = new AccountStatusBadge(AccountStatus.ENABLED, BadgeVariant.SUCCESS, CheckCircle)
    static readonly DISABLED = new AccountStatusBadge(AccountStatus.DISABLED, BadgeVariant.WARNING, AlertCircle)
    static readonly LOCKED = new AccountStatusBadge(AccountStatus.LOCKED, BadgeVariant.SECONDARY, Lock)
    static readonly DELETED = new AccountStatusBadge(AccountStatus.DELETED, BadgeVariant.DESTRUCTIVE, XCircle)

    static readonly values: ReadonlyArray<AccountStatusBadge> = [
        AccountStatusBadge.PENDING,
        AccountStatusBadge.ENABLED,
        AccountStatusBadge.DISABLED,
        AccountStatusBadge.LOCKED,
        AccountStatusBadge.DELETED,
    ]

    static of(code: AccountStatus | null | undefined): AccountStatusBadge | undefined {
        return AccountStatusBadge.values.find((v) => v.code === code)
    }
}

/* eslint-disable i18next/no-literal-string -- field name constants for backend API */
export const AccountResponseFields = {
    id: 'id',
    identity: 'identity',
    targetSystem: 'targetSystem',
    targetSystemType: 'targetSystemType',
    externalIdentifier: 'externalIdentifier',
    status: 'status',
    disabledAt: 'disabledAt',
    version: 'version',
    createdAt: 'createdAt',
    updatedAt: 'updatedAt',
} as const

export const CreateAccountRequestFields = {
    identityId: 'identityId',
    targetSystemId: 'targetSystemId',
    externalIdentifier: 'externalIdentifier',
} as const

export const AccountLifecycleResponseFields = {
    taskId: 'taskId',
} as const

export const AccountActionRequestFields = {
    reason: 'reason',
} as const

export const AccountStatusEventResponseFields = {
    id: 'id',
    accountId: 'accountId',
    fromStatus: 'fromStatus',
    toStatus: 'toStatus',
    changedAt: 'changedAt',
    actor: 'actor',
    triggerSource: 'triggerSource',
    triggerRefType: 'triggerRefType',
    triggerRefId: 'triggerRefId',
    reason: 'reason',
} as const
/* eslint-enable i18next/no-literal-string */

/**
 * Request body for the POST account action endpoints (disable / enable / lock /
 * delete). Mirror of {@code AccountActionRequest} (spec §7.4) — {@code reason}
 * is optional, capped at 1000 chars.
 */
export interface AccountActionRequest {
    reason?: string | null;
}

/**
 * API response shape for {@code GET /api/i/accounts/{id}/status-events} (spec §7.2).
 * Structurally identical to {@link AccountStatusEvent} — the wire DTO carries an
 * inline {@code actor: {id, displayName}} object resolved server-side via batch
 * lookup; the persisted entity row carries {@code actorId}. Aliased to the
 * timeline-row interface so the two stay in lock-step (no duplicated field block).
 */
export type AccountStatusEventResponse = AccountStatusEvent

/**
 * Response from account lifecycle operations (create/disable/enable/delete on target system).
 */
export interface AccountLifecycle {
    taskId: UUID;
}
